import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { m } from 'framer-motion';
import { ActionCardSkeleton } from 'src/components/actionCards';
import KPICard from './KPICard';
import { accentForKpi, stageIconForKpi } from './reportAccents';

export default function KPICardsRow({
    kpis = [],
    comparisonType = 'period',
    onKpiClick = null,
    activeKey = null,
    loading = false,
    skeletonCount = 0,
    gap = '16px',
    filterCards = false,
    minCardWidth = 150,
}) {
    if (loading) {
        const slots = skeletonCount || kpis.length || 5;
        return (
            <Box sx={{ mb: 3, display: 'flex', gap, flexWrap: 'wrap' }}>
                {Array.from({ length: slots }).map((_, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Box key={i} data-testid="kpi-card-skeleton" sx={{ flex: 1, minWidth: '150px', display: 'flex' }}>
                        <ActionCardSkeleton />
                    </Box>
                ))}
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 3 }}>
            {/* KPI Cards with staggered entrance */}
            <Box
                component={m.div}
                initial="initial"
                animate="animate"
                variants={{
                    initial: {},
                    animate: {
                        transition: {
                            staggerChildren: 0.1,
                            delayChildren: 0.05,
                        },
                    },
                }}
                sx={{
                    display: 'flex',
                    gap,
                    flexWrap: 'wrap',
                }}
            >
                {kpis.map((kpi, index) => (kpi.node ? (
                    // Custom card (e.g. the By Treatment chips carousel) sharing the row's flex slot.
                    <Box key={kpi.key || index} data-testid="kpi-custom-node" sx={{ flex: 1, minWidth: `${minCardWidth}px`, display: 'flex' }}>
                        {kpi.node}
                    </Box>
                ) : (
                    <KPICard
                        key={kpi.key || index}
                        label={kpi.label}
                        value={kpi.value}
                        previousValue={kpi.previousValue}
                        hideDelta={kpi.hideDelta}
                        deltaPercent={kpi.deltaPercent}
                        deltaInverted={kpi.deltaInverted}
                        comparisonLabel={kpi.comparisonLabel}
                        color={kpi.color}
                        accent={kpi.accent || accentForKpi(kpi.key, kpi.color)}
                        conversionRate={kpi.conversionRate}
                        prefix={kpi.prefix}
                        suffix={kpi.suffix}
                        formatValue={kpi.formatValue}
                        comparisonType={comparisonType}
                        onClick={onKpiClick && kpi.clickable !== false ? () => onKpiClick(kpi.key || kpi.label) : null}
                        tooltipText={kpi.tooltipText}
                        icon={kpi.icon}
                        iconColor={kpi.iconColor}
                        iconBg={kpi.iconBg}
                        topIcon={stageIconForKpi(kpi.key) || kpi.topIcon}
                        topIconColor={kpi.topIconColor}
                        topIconBg={kpi.topIconBg}
                        subText={kpi.subText}
                        scrollDirection={index % 2 === 0 ? 'up' : 'down'}
                        active={Boolean(activeKey) && (kpi.key || kpi.label) === activeKey}
                        filterCard={filterCards}
                        minWidth={minCardWidth}
                    />
                )))}
            </Box>
        </Box>
    );
}

KPICardsRow.propTypes = {
    minCardWidth: PropTypes.number,
    kpis: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string,
            label: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            previousValue: PropTypes.number,
            hideDelta: PropTypes.bool,
            deltaPercent: PropTypes.number,
            deltaInverted: PropTypes.bool,
            comparisonLabel: PropTypes.string,
            color: PropTypes.oneOf(['green', 'blue', 'purple', 'yellow', 'red', 'cyan']),
            conversionRate: PropTypes.number,
            prefix: PropTypes.string,
            suffix: PropTypes.string,
            formatValue: PropTypes.func,
            subText: PropTypes.string,
            tooltipText: PropTypes.string,
            topIcon: PropTypes.string,
            topIconColor: PropTypes.string,
            topIconBg: PropTypes.string,
            clickable: PropTypes.bool,
            node: PropTypes.node,
            icon: PropTypes.string,
            iconColor: PropTypes.string,
            iconBg: PropTypes.string,
        })
    ).isRequired,
    comparisonType: PropTypes.oneOf(['period', 'yoy']),
    onKpiClick: PropTypes.func,
    activeKey: PropTypes.string,
    loading: PropTypes.bool,
    skeletonCount: PropTypes.number,
    // Space between cards; a compact strip or a long row (e.g. seven filter cards) can tighten it.
    gap: PropTypes.string,
    // The cards filter the list below: strong active state and one-line labels.
    filterCards: PropTypes.bool,
};

